import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { branding } from './siteConfig.js';
import {
  ADMIN_NAV_ITEM,
  filterNavItems,
  resolveNavGroups,
} from './navMenu.js';

function buildInitialOpenSections(groups) {
  const open = {};
  groups.forEach((g) => {
    open[g.id] = Boolean(g.defaultOpen);
  });
  return open;
}

function NavDrawer({
  isOpen,
  onClose,
  isPublic,
  isAdmin,
  isMobile,
  user,
  onNavigate,
  onLogout,
}) {
  const location = useLocation();
  const groups = useMemo(() => resolveNavGroups(isPublic), [isPublic]);
  const [query, setQuery] = useState('');
  const [openSections, setOpenSections] = useState(() => buildInitialOpenSections(groups));

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      document.body.classList.remove('nav-drawer-open');
      return undefined;
    }
    document.body.classList.add('nav-drawer-open');
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('nav-drawer-open');
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setOpenSections(buildInitialOpenSections(groups));
  }, [groups]);

  const filtered = useMemo(() => filterNavItems(groups, query), [groups, query]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNav = (path) => {
    onNavigate(path);
    onClose();
  };

  if (!isOpen) return null;

  const drawer = (
    <>
      <div className="nav-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <nav
        id="site-nav-drawer"
        className="nav-drawer"
        aria-label="Site navigation"
      >
        <div className="nav-drawer-header">
          <div className="nav-drawer-brand">
            <span className="nav-drawer-brand-mark">{branding.shortName}</span>
            <div className="nav-drawer-brand-text">
              <span className="nav-drawer-brand-name">{branding.communityName}</span>
              {user ? (
                <span className={`nav-drawer-user${isAdmin ? ' nav-drawer-user--admin' : ''}`}>
                  {user.email}
                  {isAdmin ? ' · Admin' : ''}
                </span>
              ) : (
                <span className="nav-drawer-user">Browse community portal</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="nav-drawer-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <div className="nav-drawer-search-wrap">
          <span className="nav-drawer-search-icon" aria-hidden="true">⌕</span>
          <input
            type="search"
            className="nav-drawer-search"
            placeholder="Search pages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search menu links"
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className="nav-drawer-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="nav-drawer-body">
          {filtered ? (
            <div className="nav-drawer-results">
              {filtered.length === 0 ? (
                <p className="nav-drawer-empty">No matching links</p>
              ) : (
                filtered.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    className={`nav-drawer-link${location.pathname === item.path ? ' nav-drawer-link--active' : ''}`}
                    onClick={() => handleNav(item.path)}
                  >
                    <span className="nav-drawer-link-label">{item.label}</span>
                    <span className="nav-drawer-link-meta">{item.groupLabel}</span>
                  </button>
                ))
              )}
            </div>
          ) : (
            groups.map((group) => {
              const isExpanded = openSections[group.id];
              const activeInGroup = group.items.some((item) => item.path === location.pathname);
              return (
                <section
                  key={group.id}
                  className={`nav-drawer-group${activeInGroup ? ' nav-drawer-group--has-active' : ''}`}
                >
                  <button
                    type="button"
                    className="nav-drawer-group-toggle"
                    onClick={() => toggleSection(group.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className="nav-drawer-group-title">{group.label}</span>
                    <span className="nav-drawer-chevron" aria-hidden="true">
                      {isExpanded ? '▾' : '▸'}
                    </span>
                  </button>
                  {isExpanded ? (
                    <div className="nav-drawer-group-items">
                      {group.items.map((item) => (
                        <button
                          key={item.path}
                          type="button"
                          className={`nav-drawer-link${location.pathname === item.path ? ' nav-drawer-link--active' : ''}`}
                          onClick={() => handleNav(item.path)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })
          )}

          {!isPublic && isAdmin ? (
            <section className="nav-drawer-group nav-drawer-group--admin">
              <p className="nav-drawer-group-label">Administration</p>
              <div className="nav-drawer-group-items nav-drawer-group-items--single">
                <button
                  type="button"
                  className={`nav-drawer-link nav-drawer-link--admin${location.pathname === ADMIN_NAV_ITEM.path ? ' nav-drawer-link--active' : ''}`}
                  onClick={() => handleNav(ADMIN_NAV_ITEM.path)}
                >
                  {ADMIN_NAV_ITEM.label}
                </button>
              </div>
            </section>
          ) : null}
        </div>

        {!isPublic ? (
          <div className="nav-drawer-footer">
            {isMobile ? (
              <button type="button" className="nav-drawer-logout" onClick={onLogout}>
                Sign out
              </button>
            ) : (
              <p className="nav-drawer-footer-hint">Press Esc to close</p>
            )}
          </div>
        ) : null}
      </nav>
    </>
  );

  return createPortal(drawer, document.body);
}

export default NavDrawer;